// Cross-platform nav-app deep-link helper for mobile-mode route view.
// One entry point for "Directions" buttons + post-finish "Navigate to Next Stop" CTA.
//
// v1: device default — iOS → Apple Maps, Android → user's default maps app.
// v1.1: per-tech preference (Apple Maps / Google Maps / Waze) — TODO.

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const buildAddress = (stop) => {
  if (!stop) return '';
  if (stop.address) return stop.address;
  const parts = [stop.street, stop.city, stop.state, stop.zip].filter(Boolean);
  return parts.join(', ');
};

const hasCoords = (stop) =>
  stop && typeof stop.lat === 'number' && typeof stop.lng === 'number';

const buildUrl = (platform, preference, stop, origin) => {
  const address = buildAddress(stop);
  const encoded = encodeURIComponent(address);
  const coords = hasCoords(stop) ? `${stop.lat},${stop.lng}` : null;

  // Origin (FROM) — pre-fills the source so the maps app opens with the
  // route ready. `origin` may be { lat, lng } (preferred — exact GPS fix)
  // or omitted (lets the maps app default to its own "current location").
  const originCoords = origin && typeof origin.lat === 'number' && typeof origin.lng === 'number'
    ? `${origin.lat},${origin.lng}`
    : null;

  if (preference === 'waze' && (coords || address)) {
    // Waze always uses the device's current location as source — no saddr param.
    return coords
      ? `waze://?ll=${coords}&navigate=yes`
      : `https://waze.com/ul?q=${encoded}&navigate=yes`;
  }

  if (preference === 'google' || platform === 'desktop') {
    const originParam = originCoords ? `&origin=${originCoords}` : '';
    return `https://www.google.com/maps/dir/?api=1${originParam}&destination=${encoded}&travelmode=driving`;
  }

  if (platform === 'ios') {
    // Apple Maps via maps: scheme; saddr/daddr accept addresses or coords.
    const saddrParam = originCoords ? `saddr=${originCoords}&` : '';
    return `maps://?${saddrParam}daddr=${encoded}&dirflg=d`;
  }

  if (platform === 'android') {
    // Android geo: scheme doesn't support origin — current location is implicit.
    return coords
      ? `geo:${coords}?q=${coords}(${encoded})`
      : `geo:0,0?q=${encoded}`;
  }

  const originParam = originCoords ? `&origin=${originCoords}` : '';
  return `https://www.google.com/maps/dir/?api=1${originParam}&destination=${encoded}&travelmode=driving`;
};

/**
 * Open the device's nav app routed to the given stop.
 * @param {Object} stop - { address?, street?, city?, state?, zip?, lat?, lng? }
 * @param {Object} options - { preference?: 'apple' | 'google' | 'waze' | 'default',
 *                              origin?: { lat, lng } }
 *   - origin: when provided, pre-fills the source/FROM location so the maps
 *     app opens with the full route ready. Pass GPS coords (typically from
 *     getCurrentPosition) for the best result. Ignored for Waze and Android
 *     geo: which always use the device's current location implicitly.
 */
export const openNavigation = (stop, options = {}) => {
  if (!stop) return;
  const preference = options.preference || 'default';
  const platform = detectPlatform();
  const url = buildUrl(platform, preference, stop, options.origin);
  window.location.href = url;
};

// Exposed for tests + the Map sub-view's per-pin "Directions" affordance.
export const _internals = { detectPlatform, buildAddress, hasCoords, buildUrl };

export default openNavigation;
